import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Users,
  Briefcase,
  DollarSign,
  Heart,
  CheckCircle,
  XCircle,
  Clock,
  Edit,
  Send,
  Download,
  History,
  User,
  AlertCircle,
  Eye,
  GitBranch,
  Calendar,
  Loader2,
  BarChart3,
} from "lucide-react";
import BilanComments from "../components/BilanComments";
import PDFPreview from "../components/PDFPreview";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BilanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isCabinet } = useAuth();
  const [bilan, setBilan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  
  // État pour les versions
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  
  // État pour la prévisualisation PDF
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: null, filename: null });

  useEffect(() => {
    fetchBilan();
  }, [id]);

  const fetchBilan = async () => {
    try {
      const response = await axios.get(`${API_URL}/bilans/${id}`);
      setBilan(response.data);
    } catch (error) {
      console.error(/* PROD */ "Failed to fetch bilan:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Charger les versions du bilan
  const fetchVersions = async () => {
    setVersionsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/bilans/${id}/versions`);
      setVersions(response.data.versions || []);
    } catch (error) {
      console.error(/* PROD */ "Failed to fetch versions:", error);
      toast.error("Erreur lors du chargement des versions");
    } finally {
      setVersionsLoading(false);
    }
  };

  // Prévisualiser un document PDF
  const handlePreviewPdf = async (documentType) => {
    try {
      let url, filename;
      
      if (documentType === "quittance") {
        url = `${API_URL}/bilans/${id}/quittance`;
        filename = `Quittance_BilanSocial_${bilan.enterprise_name || 'N-A'}_${bilan.annee}.pdf`;
      } else if (documentType === "rapport") {
        url = `${API_URL}/bilans/${id}/rapport`;
        filename = `Rapport_Analyse_BilanSocial_${bilan.enterprise_name || 'N-A'}_${bilan.annee}.pdf`;
      } else if (documentType === "preuve_paiement") {
        url = `${API_URL}/bilans/${id}/preuve-paiement`;
        filename = bilan.preuve_paiement_10_filename || "Preuve_Paiement.pdf";
      }
      
      // Get PDF as blob URL for preview
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      
      setPdfPreview({ open: true, url: blobUrl, filename });
    } catch (error) {
      console.error(/* PROD */ "Error loading PDF:", error);
      toast.error("Erreur lors du chargement du PDF");
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview.url) {
      window.URL.revokeObjectURL(pdfPreview.url);
    }
    setPdfPreview({ open: false, url: null, filename: null });
  };

  const handleValidate = async (action) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/bilans/${id}/validate`, {
        action,
        reason: action === "rejeter" ? rejectReason : undefined,
      });
      const messages = {
        valider: "Bilan validé avec succès. Le cabinet a été notifié par email.",
        rejeter: "Bilan rejeté. Le cabinet a été notifié par email avec le motif du rejet.",
        controler: "Bilan mis en contrôle. Le cabinet a été notifié."
      };
      toast.success(messages[action] || "Action effectuée");
      setShowRejectDialog(false);
      setRejectReason("");
      fetchBilan();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/bilans/${id}/submit`);
      toast.success("Bilan soumis avec succès");
      fetchBilan();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la soumission");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async (documentType) => {
    const isRapport = documentType === "rapport";
    const prefix = isRapport ? "Rapport_Analyse_BilanSocial" : "Quittance_BilanSocial";
    try {
      const response = await axios.get(`${API_URL}/bilans/${id}/${documentType}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${prefix}_${bilan.enterprise_name || 'N-A'}_${bilan.annee}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isRapport ? "Rapport d'analyse téléchargé avec succès" : "Quittance téléchargée avec succès");
    } catch (error) {
      toast.error(isRapport ? "Erreur lors du téléchargement du rapport" : "Erreur lors du téléchargement de la quittance");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      brouillon: { class: "badge-neutral", label: "Brouillon", icon: FileText },
      soumis: { class: "badge-warning", label: "Soumis", icon: Clock },
      en_controle: { class: "badge-warning", label: "En contrôle", icon: Clock },
      valide: { class: "badge-success", label: "Validé", icon: CheckCircle },
      rejete: { class: "badge-error", label: "Rejeté", icon: XCircle },
    };
    const { class: className, label, icon: Icon } = config[status] || config.brouillon;
    return (
      <span className={`${className} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-60 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!bilan) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Bilan non trouvé</p>
        <Link to="/bilans">
          <Button className="mt-4">Retour aux bilans</Button>
        </Link>
      </div>
    );
  }

  const { rotation, absenteeism } = bilan.indicators || {};
  const canEdit = isCabinet && (bilan.status === "brouillon" || bilan.status === "rejete");
  const canSubmit = isCabinet && bilan.status === "brouillon";
  const canValidate = isAdmin && (bilan.status === "soumis" || bilan.status === "en_controle");
  const canDownload = bilan.status === "valide";

  return (
    <div className="space-y-6 pb-16" data-testid="bilan-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/bilans">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-slate-900">
                Bilan Social {bilan.annee}
              </h1>
              {getStatusBadge(bilan.status)}
            </div>
            <p className="text-muted-foreground mt-1">
              {bilan.enterprise_name || "Entreprise"} • {bilan.region || "N/A"}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          {canEdit && (
            <Link to={`/bilans/${id}/modifier`}>
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </Link>
          )}
          {canSubmit && (
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={handleSubmit}
              disabled={actionLoading}
              data-testid="submit-bilan-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Soumettre
            </Button>
          )}
          {canValidate && (
            <div className="flex gap-2">
              {bilan.status === "soumis" && (
                <Button
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => handleValidate("controler")}
                  disabled={actionLoading}
                  data-testid="control-bilan-btn"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Mettre en contrôle
                </Button>
              )}
              <Button
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setShowRejectDialog(true)}
                disabled={actionLoading}
                data-testid="reject-bilan-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Rejeter
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleValidate("valider")}
                disabled={actionLoading}
                data-testid="validate-bilan-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Valider
              </Button>
            </div>
          )}
          {canDownload && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => handlePreviewPdf("quittance")} data-testid="preview-quittance-btn">
                <Eye className="w-4 h-4 mr-2" />
                Aperçu Quittance
              </Button>
              <Button variant="outline" onClick={() => handleDownloadPdf("quittance")} data-testid="download-quittance-btn">
                <Download className="w-4 h-4 mr-2" />
                Quittance
              </Button>
              <Button variant="outline" onClick={() => handlePreviewPdf("rapport")} data-testid="preview-rapport-btn">
                <Eye className="w-4 h-4 mr-2" />
                Aperçu Rapport
              </Button>
              <Button className="bg-primary hover:bg-primary/90" onClick={() => handleDownloadPdf("rapport")} data-testid="download-rapport-btn">
                <BarChart3 className="w-4 h-4 mr-2" />
                Rapport d'analyse
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Status Banner for Admin */}
      {isAdmin && bilan.status === "soumis" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">Bilan en attente de traitement</p>
              <p className="text-sm text-blue-700 mt-1">
                Ce bilan a été soumis le {bilan.submitted_at ? new Date(bilan.submitted_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}. 
                Vous pouvez le mettre en contrôle, le valider ou le rejeter.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status Banner for En Contrôle */}
      {bilan.status === "en_controle" && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">Bilan en cours de contrôle</p>
              <p className="text-sm text-amber-700 mt-1">
                Ce bilan est actuellement en cours de vérification par l'IGT.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rejection reason */}
      {bilan.status === "rejete" && bilan.rejection_reason && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Motif du rejet</p>
            <p className="text-sm text-red-700 mt-1">{bilan.rejection_reason}</p>
          </div>
        </div>
      )}

      {/* Preuve de Paiement des 10% */}
      {bilan.preuve_paiement_10_filename && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-emerald-800">Preuve de Paiement des 10%</p>
            <p className="text-sm text-emerald-700 mt-1">
              Document: {bilan.preuve_paiement_10_filename}
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
            onClick={() => handlePreviewPdf("preuve_paiement")}
          >
            <Eye className="w-4 h-4 mr-1" />
            Aperçu
          </Button>
        </div>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Effectifs */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Effectifs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Guinéens Hommes</p>
                <p className="font-semibold">{bilan.effectifs?.guinéens_hommes || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Guinéens Femmes</p>
                <p className="font-semibold">{bilan.effectifs?.guinéens_femmes || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Étrangers Hommes</p>
                <p className="font-semibold">{bilan.effectifs?.etrangers_hommes || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Étrangers Femmes</p>
                <p className="font-semibold">{bilan.effectifs?.etrangers_femmes || 0}</p>
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">CDD</p>
                <p className="font-semibold">{bilan.effectifs?.cdd || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">CDI</p>
                <p className="font-semibold">{bilan.effectifs?.cdi || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Handicapés</p>
                <p className="font-semibold">{bilan.effectifs?.handicapes || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Temporaires</p>
                <p className="font-semibold">{bilan.effectifs?.temporaires || 0}</p>
              </div>
            </div>
            
            {/* Emplois Indirects */}
            {(bilan.effectifs?.emplois_indirects > 0 || bilan.effectifs?.emplois_indirects_details) && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-blue-800 mb-2">Emplois Indirects</p>
                <p className="text-2xl font-bold text-blue-700">{bilan.effectifs?.emplois_indirects || 0}</p>
                {bilan.effectifs?.emplois_indirects_details && (
                  <p className="text-sm text-blue-600 mt-2">{bilan.effectifs.emplois_indirects_details}</p>
                )}
              </div>
            )}
            
            {/* Tranches d'Âge */}
            <Separator />
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-3">Tranches d'Âge</p>
              <div className="grid grid-cols-4 gap-2">
                <div className="p-2 bg-indigo-50 rounded-lg text-center">
                  <p className="text-xs text-indigo-600">16-21 ans</p>
                  <p className="font-semibold text-indigo-800">{bilan.effectifs?.age_16_21 || 0}</p>
                </div>
                <div className="p-2 bg-indigo-50 rounded-lg text-center">
                  <p className="text-xs text-indigo-600">22-30 ans</p>
                  <p className="font-semibold text-indigo-800">{bilan.effectifs?.age_22_30 || 0}</p>
                </div>
                <div className="p-2 bg-indigo-50 rounded-lg text-center">
                  <p className="text-xs text-indigo-600">31-40 ans</p>
                  <p className="font-semibold text-indigo-800">{bilan.effectifs?.age_31_40 || 0}</p>
                </div>
                <div className="p-2 bg-indigo-50 rounded-lg text-center">
                  <p className="text-xs text-indigo-600">41-50 ans</p>
                  <p className="font-semibold text-indigo-800">{bilan.effectifs?.age_41_50 || 0}</p>
                </div>
                <div className="p-2 bg-indigo-50 rounded-lg text-center">
                  <p className="text-xs text-indigo-600">51-60 ans</p>
                  <p className="font-semibold text-indigo-800">{bilan.effectifs?.age_51_60 || 0}</p>
                </div>
                <div className="p-2 bg-indigo-50 rounded-lg text-center">
                  <p className="text-xs text-indigo-600">61-65 ans</p>
                  <p className="font-semibold text-indigo-800">{bilan.effectifs?.age_61_65 || 0}</p>
                </div>
                <div className="p-2 bg-indigo-50 rounded-lg text-center col-span-2">
                  <p className="text-xs text-indigo-600">Plus de 65 ans</p>
                  <p className="font-semibold text-indigo-800">{bilan.effectifs?.age_plus_65 || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Emploi */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-primary" />
              Emploi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Cadres</p>
                <p className="font-semibold">{bilan.emploi?.cadres || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Agents Maîtrise</p>
                <p className="font-semibold">{bilan.emploi?.agents_maitrise || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Employés</p>
                <p className="font-semibold">{bilan.emploi?.employes || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Ouvriers</p>
                <p className="font-semibold">{bilan.emploi?.ouvriers || 0}</p>
              </div>
            </div>
            <Separator />
            <p className="text-sm font-semibold text-muted-foreground mb-2">Mouvements de Personnel</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <p className="text-xs text-emerald-700">Embauches</p>
                <p className="font-semibold text-emerald-700">{bilan.emploi?.embauches || 0}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-xs text-red-700">Licenciements</p>
                <p className="font-semibold text-red-700">{bilan.emploi?.licenciements || 0}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-xs text-amber-700">Démissions</p>
                <p className="font-semibold text-amber-700">{bilan.emploi?.demissions || 0}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg text-center">
                <p className="text-xs text-orange-700">Retraites</p>
                <p className="font-semibold text-orange-700">{bilan.emploi?.retraites || 0}</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg text-center">
                <p className="text-xs text-gray-700">Décès</p>
                <p className="font-semibold text-gray-700">{bilan.emploi?.deces || 0}</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg text-center">
                <p className="text-xs text-purple-700">Fin de Contrat</p>
                <p className="font-semibold text-purple-700">{bilan.emploi?.fin_contrat || 0}</p>
              </div>
            </div>
            {/* Rotation du personnel */}
            <Separator />
            <div className="p-4 bg-primary/10 rounded-lg">
              <p className="text-sm font-semibold text-primary mb-3">Taux de Rotation</p>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Effectif 1er Jan</p>
                  <p className="font-semibold" data-testid="rotation-effectif-debut">{rotation?.effectif_debut ?? 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Départs</p>
                  <p className="font-semibold" data-testid="rotation-departs">{rotation?.departs ?? 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Arrivées</p>
                  <p className="font-semibold" data-testid="rotation-arrivees">{rotation?.arrivees ?? 0}</p>
                </div>
                <div className="text-center bg-primary/20 rounded-lg p-2">
                  <p className="text-xs text-primary font-medium">Taux</p>
                  <p className="font-bold text-primary text-lg" data-testid="rotation-taux">{rotation?.formatted ?? "N/R"}</p>
                </div>
              </div>
              {rotation?.note && (
                <p className="mt-2 text-xs text-muted-foreground text-center" data-testid="rotation-formule">{rotation.note}</p>
              )}
              <div className="mt-3 p-2 bg-white rounded text-center">
                <p className="text-xs text-muted-foreground">Postes Nationalisés</p>
                <p className="font-semibold text-primary">{bilan.emploi?.postes_nationalises || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Absentéisme */}
        {bilan.absenteisme && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Absentéisme
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center">
                    <p className="text-xs text-amber-700">Jours contractuels</p>
                    <p className="font-semibold">{bilan.absenteisme.jours_contractuels || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-amber-700">Jours congés</p>
                    <p className="font-semibold">{bilan.absenteisme.jours_conges || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-amber-700">Jours absence</p>
                    <p className="font-semibold">{bilan.absenteisme.jours_absence || 0}</p>
                  </div>
                  <div className="text-center bg-amber-100 rounded-lg p-2">
                    <p className="text-xs text-amber-800 font-medium">Taux</p>
                    <p className="font-bold text-amber-800 text-lg" data-testid="absenteisme-taux">{absenteeism?.formatted ?? "Non renseigné"}</p>
                  </div>
                </div>
                {absenteeism?.note && (
                  <p className="mt-2 text-xs text-amber-700 text-center" data-testid="absenteisme-formule">{absenteeism.note}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rémunération */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Rémunération
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-primary/5 rounded-lg">
                <p className="text-sm text-muted-foreground">Masse Salariale Annuelle</p>
                <p className="text-2xl font-bold text-primary">
                  {(bilan.remuneration?.masse_salariale || 0).toLocaleString("fr-FR")} GNF
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Heures Supp.</p>
                  <p className="font-semibold">{bilan.remuneration?.heures_supplementaires || 0}h</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Primes</p>
                  <p className="font-semibold">
                    {(bilan.remuneration?.primes_versees || 0).toLocaleString("fr-FR")} GNF
                  </p>
                </div>
              </div>
              {/* Charges Sociales */}
              <Separator />
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-semibold text-blue-800 mb-3">Charges Sociales et Fiscales</p>
                <div className="grid grid-cols-5 gap-2">
                  <div className="text-center">
                    <p className="text-xs text-blue-700">CNSS</p>
                    <p className="font-semibold text-sm">{(bilan.remuneration?.total_cnss || 0).toLocaleString("fr-FR")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-blue-700">VF</p>
                    <p className="font-semibold text-sm">{(bilan.remuneration?.total_vf || 0).toLocaleString("fr-FR")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-blue-700">CFPCA</p>
                    <p className="font-semibold text-sm">{(bilan.remuneration?.total_cfpca || 0).toLocaleString("fr-FR")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-blue-700">TA</p>
                    <p className="font-semibold text-sm">{(bilan.remuneration?.total_ta || 0).toLocaleString("fr-FR")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-blue-700">RTS</p>
                    <p className="font-semibold text-sm">{(bilan.remuneration?.total_rts || 0).toLocaleString("fr-FR")}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Santé Sécurité */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              Santé & Sécurité
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-sm text-red-700">Accidents de Travail</p>
                  <p className="text-2xl font-bold text-red-700">
                    {bilan.sante_securite?.accidents_travail || 0}
                  </p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-sm text-amber-700">Maladies Prof.</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {bilan.sante_securite?.maladies_professionnelles || 0}
                  </p>
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">Jours d'Arrêt Total</p>
                <p className="text-xl font-bold">{bilan.sante_securite?.jours_arret || 0} jours</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commentaires et Observations */}
      <BilanComments bilanId={id} bilanStatus={bilan.status} />

      {/* Historique des modifications */}
      {bilan.historique && bilan.historique.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Historique des modifications
            </CardTitle>
            <CardDescription>
              Traçabilité complète de toutes les actions sur ce bilan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {bilan.historique.slice().reverse().map((entry, index) => {
                const actionColors = {
                  creation: "bg-blue-100 text-blue-800 border-blue-200",
                  modification: "bg-amber-100 text-amber-800 border-amber-200",
                  soumission: "bg-purple-100 text-purple-800 border-purple-200",
                  validation: "bg-green-100 text-green-800 border-green-200",
                  rejet: "bg-red-100 text-red-800 border-red-200",
                  en_controle: "bg-orange-100 text-orange-800 border-orange-200",
                };
                
                const actionLabels = {
                  creation: "Création",
                  modification: "Modification",
                  soumission: "Soumission",
                  validation: "Validation",
                  rejet: "Rejet",
                  en_controle: "Mise en contrôle",
                };
                
                const actionIcons = {
                  creation: <FileText className="w-4 h-4" />,
                  modification: <Edit className="w-4 h-4" />,
                  soumission: <Send className="w-4 h-4" />,
                  validation: <CheckCircle className="w-4 h-4" />,
                  rejet: <XCircle className="w-4 h-4" />,
                  en_controle: <AlertCircle className="w-4 h-4" />,
                };
                
                return (
                  <div 
                    key={index} 
                    className={`p-4 rounded-lg border ${actionColors[entry.action] || "bg-gray-100 border-gray-200"}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-full shadow-sm">
                          {actionIcons[entry.action] || <History className="w-4 h-4" />}
                        </div>
                        <div>
                          <p className="font-semibold">
                            {actionLabels[entry.action] || entry.action}
                          </p>
                          <p className="text-sm opacity-80">{entry.details}</p>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-medium flex items-center gap-1 justify-end">
                          <User className="w-3 h-3" />
                          {entry.user_name || "Utilisateur"}
                        </p>
                        <p className="text-xs opacity-70">
                          {entry.user_role === "admin_igt" ? "Admin IGT" : "Cabinet"}
                        </p>
                        <p className="text-xs opacity-70 mt-1">
                          {new Date(entry.timestamp).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Commentaires */}
      {bilan.commentaires && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Commentaires</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600">{bilan.commentaires}</p>
          </CardContent>
        </Card>
      )}

      {/* Section Historique des Versions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-purple-600" />
              Versions du Bilan
            </CardTitle>
            <CardDescription>
              Consultez les différentes versions sauvegardées de ce bilan
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchVersions}
            disabled={versionsLoading}
          >
            {versionsLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <History className="w-4 h-4 mr-2" />
            )}
            {versions.length > 0 ? "Actualiser" : "Charger les versions"}
          </Button>
        </CardHeader>
        <CardContent>
          {versionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Cliquez sur "Charger les versions" pour voir l'historique</p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version, index) => (
                <div
                  key={version.id}
                  className={`p-4 rounded-lg border transition-all cursor-pointer hover:border-purple-300 ${
                    selectedVersion?.id === version.id ? "border-purple-500 bg-purple-50" : "border-gray-200"
                  }`}
                  onClick={() => setSelectedVersion(selectedVersion?.id === version.id ? null : version)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-600"
                      }`}>
                        v{version.version_number}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          Version {version.version_number}
                          {index === 0 && <span className="ml-2 text-xs text-purple-600 font-normal">(Plus récente)</span>}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {new Date(version.created_at).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                        <User className="w-3 h-3" />
                        {version.created_by_name || "Utilisateur"}
                      </p>
                      {version.reason && (
                        <p className="text-xs text-amber-600 mt-1">{version.reason}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Détails de la version (expandable) */}
                  {selectedVersion?.id === version.id && version.snapshot && (
                    <div className="mt-4 pt-4 border-t border-purple-200">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-gray-500 mb-1">Effectif Total</p>
                          <p className="font-semibold">
                            {(version.snapshot.effectifs?.guinéens_hommes || 0) +
                             (version.snapshot.effectifs?.guinéens_femmes || 0) +
                             (version.snapshot.effectifs?.etrangers_hommes || 0) +
                             (version.snapshot.effectifs?.etrangers_femmes || 0)}
                          </p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-gray-500 mb-1">Masse Salariale</p>
                          <p className="font-semibold">
                            {(version.snapshot.remuneration?.masse_salariale || 0).toLocaleString("fr-FR")} GNF
                          </p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-gray-500 mb-1">Accidents</p>
                          <p className="font-semibold">
                            {version.snapshot.sante_securite?.accidents_travail || 0}
                          </p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-gray-500 mb-1">Taux Absentéisme</p>
                          <p className="font-semibold">
                            {version.snapshot.absenteisme?.taux_absenteisme || 0}%
                          </p>
                        </div>
                      </div>
                      
                      {/* Emploi détails */}
                      {version.snapshot.emploi && (
                        <div className="mt-3 p-2 bg-white rounded border">
                          <p className="text-xs text-gray-500 mb-2">Mouvements d'emploi</p>
                          <div className="grid grid-cols-4 gap-2 text-xs">
                            <div className="text-center">
                              <span className="text-green-600 font-medium">+{version.snapshot.emploi.embauches_cdi || 0}</span>
                              <p className="text-gray-400">Embauches CDI</p>
                            </div>
                            <div className="text-center">
                              <span className="text-blue-600 font-medium">+{version.snapshot.emploi.embauches_cdd || 0}</span>
                              <p className="text-gray-400">Embauches CDD</p>
                            </div>
                            <div className="text-center">
                              <span className="text-red-600 font-medium">-{version.snapshot.emploi.departs_demission || 0}</span>
                              <p className="text-gray-400">Démissions</p>
                            </div>
                            <div className="text-center">
                              <span className="text-orange-600 font-medium">-{version.snapshot.emploi.departs_licenciement || 0}</span>
                              <p className="text-gray-400">Licenciements</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* PDF Preview Dialog */}
      <PDFPreview
        url={pdfPreview.url}
        filename={pdfPreview.filename}
        open={pdfPreview.open}
        onClose={closePdfPreview}
      />

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Rejeter le Bilan</DialogTitle>
            <DialogDescription>
              Veuillez indiquer le motif du rejet. Cette information sera communiquée au cabinet.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Motif du rejet *</Label>
            <Textarea
              id="reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Décrivez les raisons du rejet..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleValidate("rejeter")}
              disabled={!rejectReason || actionLoading}
            >
              Confirmer le Rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
